import { useCallback } from 'react'
import { getFirAuth, getActionCodeSettings } from '../../Api/firebase/firebaseConnect'
import { authFns } from '../../Api/firebase/auth'
import {
  executeWithRetry,
  withTimeout,
  AUTH_TIMEOUT,
  handleAuthError,
  clearQueryParams,
  getStoredTempEmail,
  setStoredTempEmail,
  removeStoredTempEmail,
  getCurrentUrl,
  getCurrentHostname,
} from '../../Utils'
import { isNavigatorOnline } from '../../Utils/networking/networkHelpers'
import { ANALYTICS_TAGS } from '../../Constant'
import type { EmailAuthOperations } from './types'

export const useEmailAuth = (
  toggleShowSnackbar: (snackbarData: {
    text: string
    color: 'red' | 'blue' | 'green'
  }) => void,
  getCookie: (name: string) => string | null,
  setCookie: (name: string, value: string) => void,
  removeCookie: (name: string) => void
): EmailAuthOperations => {
  const handleEmailLink = useCallback(async (): Promise<void> => {
    try {
      const auth = await getFirAuth()
      if (!auth) {
        handleAuthError(
          ANALYTICS_TAGS.auth_change_error,
          'Firebase auth not available',
          'handleEmailLink'
        )
        return
      }

      const email = getStoredTempEmail(getCookie)

      if (!email) {
        handleAuthError(
          ANALYTICS_TAGS.login_error,
          'No email found',
          'handleEmailLink',
          {
            provider: 'email',
          }
        )
        toggleShowSnackbar({
          text: 'Email verification failed. Please request a new login link.',
          color: 'red',
        })
        return
      }

      const { signInWithEmailLink } = await authFns
      const result = await signInWithEmailLink(auth, email, getCurrentUrl())

      if (result?.user?.uid) {
        removeStoredTempEmail(removeCookie)
        clearQueryParams(['apiKey', 'oobCode', 'mode'])
      }
    } catch (err) {
      handleAuthError(ANALYTICS_TAGS.login_error, err, 'handleEmailLink', {
        provider: 'email',
      })
      toggleShowSnackbar({
        text: 'Email signin failed. Please try again or use a different method.',
        color: 'red',
      })
    }
  }, [])

  const signInWithEmLink = useCallback(
    async (redirectTo: string, email: string): Promise<boolean> => {
      try {
        if (!email || !email.includes('@')) {
          handleAuthError(
            ANALYTICS_TAGS.login_error,
            'Invalid email format',
            'signInWithEmLink',
            {
              provider: 'email',
            }
          )
          toggleShowSnackbar({
            text: 'Please enter a valid email address.',
            color: 'red',
          })
          return false
        }

        // Check network connectivity
        if (!isNavigatorOnline()) {
          toggleShowSnackbar({
            text: 'Network connection required. Please check your connection.',
            color: 'red',
          })
          return false
        }

        const auth = await withTimeout(
          getFirAuth(),
          AUTH_TIMEOUT,
          'getFirAuth for email link'
        )

        if (!auth) {
          handleAuthError(
            ANALYTICS_TAGS.auth_change_error,
            'Firebase auth not available',
            'signInWithEmLink'
          )
          toggleShowSnackbar({
            text: 'Auth service unavailable. Please try again later.',
            color: 'red',
          })
          return false
        }

        const actionCodeSettings = getActionCodeSettings(
          redirectTo || getCurrentHostname()
        )

        // Execute email sending with timeout and retry
        const sendEmailWithRetry = async (): Promise<void> => {
          const { sendSignInLinkToEmail } = await authFns
          await withTimeout(
            sendSignInLinkToEmail(auth, email, actionCodeSettings),
            AUTH_TIMEOUT,
            'sendSignInLinkToEmail'
          )
        }

        await executeWithRetry(sendEmailWithRetry, `signInWithEmLink_${email}`)
        setStoredTempEmail(email, setCookie)

        return true
      } catch (err) {
        handleAuthError(ANALYTICS_TAGS.login_error, err, 'signInWithEmLink', {
          provider: 'email',
        })
        toggleShowSnackbar({
          text: 'Failed to send signin link. Please try again.',
          color: 'red',
        })
        return false
      }
    },
    []
  )

  return {
    signInWithEmLink,
    handleEmailLink,
  }
}
